import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Input, toast } from '@heroui/react';

import Panel from '../Site/Panel';
import PlayerName from '../Site/PlayerName';
import AlertPanel from '../Site/AlertPanel';
import Link from '../Navigation/Link';
import Messages from '../GameBoard/Messages';
import SelectDeckModal from './SelectDeckModal';
import {
    lobbyLeaveGameRequested,
    lobbySendMessage,
    lobbyStartGameRequested
} from '../../redux/socketActions';
import { lobbyActions } from '../../redux/slices/lobbySlice';
import PendingGamePlayers from './PendingGamePlayers';
import { getPendingGameJoinAlert } from './pendingGameAlerts';
import { Constants } from '../../constants';

import PlayerJoinedMp3 from '../../assets/sound/player-joined.mp3';
import PlayerJoinedOgg from '../../assets/sound/player-joined.ogg';

/**
 * ARCHON (F9): how hard the practice bot should be.
 *
 * Difficulty is the DECK the bot brings, not its brain - the ARI band it
 * draws from. Mirrored from `server/services/botgames/difficulty`; the
 * server normalises anything it does not recognise to Medium, so the two
 * lists drifting apart cannot break a table.
 */
const BOT_DIFFICULTIES = [
    { key: 'easy', label: 'Easy', range: '45-65' },
    { key: 'medium', label: 'Medium', range: '66-89' },
    { key: 'hard', label: 'Hard', range: '90-125' }
];

function showNotification(notification) {
    if (!window.Notification || Notification.permission !== 'granted') {
        return;
    }

    try {
        const windowNotification = new Notification('Archon Arena', notification);

        setTimeout(() => windowNotification.close(), 5000);
    } catch {
        // Some browsers (e.g. Chrome on Linux/Android, or any context with an active service
        // worker) disallow the direct `Notification` constructor and throw "Illegal constructor".
        // Fall back to ServiceWorkerRegistration.showNotification when available.
        if (navigator.serviceWorker?.ready) {
            navigator.serviceWorker.ready
                .then((reg) => reg.showNotification('Archon Arena', notification))
                .catch(() => {});
        }
    }
}

const PendingGame = () => {
    const currentGame = useSelector((state) => state.lobby.currentGame);
    const user = useSelector((state) => state.account.user);
    const connecting = useSelector((state) => state.games.connecting);
    const gameError = useSelector((state) => state.lobby.gameError);
    const gameHost = useSelector((state) => state.games.gameHost);
    const notification = useRef();
    const [waiting, setWaiting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [message, setMessage] = useState('');
    const [canScroll, setCanScroll] = useState(true);
    const [playerCount, setPlayerCount] = useState(0);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const messageRef = useRef(null);

    useEffect(() => {
        if (!user) {
            return;
        }

        const players = Object.values(currentGame.players).length;
        const alert = getPendingGameJoinAlert({
            game: currentGame,
            username: user.username,
            previousPlayerCount: playerCount
        });

        if (alert && notification.current) {
            const promise = notification.current?.play();

            if (promise !== undefined) {
                promise.catch(() => {}).then(() => {});
            }

            if (alert.notify) {
                showNotification({
                    body: alert.body,
                    // The pending-game player summary exposes `avatar`/`name`, not
                    // `username`; using username produced /img/avatar/undefined.png.
                    icon: `/img/avatar/${alert.opponent.avatar}.png`
                });
            }
        }

        setPlayerCount(players);

        if (canScroll && messageRef.current) {
            messageRef.current.scrollTop = messageRef.current.scrollHeight;
        }

        if (connecting) {
            setWaiting(false);
        }
    }, [
        currentGame.owner,
        currentGame.players,
        user,
        playerCount,
        currentGame,
        canScroll,
        connecting
    ]);

    useEffect(() => {
        if (currentGame && currentGame.gameFormat === 'sealed') {
            dispatch(lobbySendMessage('getsealeddeck', currentGame.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!currentGame) {
        return null;
    }

    const deckFilter = {};
    let expansions = [];

    if (currentGame.gameFormat !== 'alliance') {
        deckFilter.isAlliance = false;
    }

    if (currentGame.gameFormat === 'unchained') {
        expansions = Constants.Expansions.filter((e) => e.value === '601');
    } else {
        expansions = Constants.Expansions.filter((e) => e.value !== '601');
    }

    deckFilter.expansion = expansions;

    // ARCHON: in a SAS-bound game the picker only lists what the game will
    // accept - the same range the server enforces on selection.
    if (currentGame.sasBound) {
        deckFilter.sasMin = currentGame.sasBound.min;
        deckFilter.sasMax = currentGame.sasBound.max;
    }

    const isLuckyDice = !!currentGame.luckyDice;

    // ARCHON (F9): the Helper Bot owns its practice table but has no hands to
    // press Start with, so at that table the player who joined holds the
    // button. Picking a deck normally starts it before they reach for it -
    // this is what stops the button being a decoration they cannot use.
    const isBotTable = !!currentGame.botGame;
    const isSeatedHere = Object.values(currentGame.players || {}).some(
        (player) => player.name === user?.username
    );
    const botDifficulty = currentGame.botDifficulty || 'medium';
    const selectedDifficulty =
        BOT_DIFFICULTIES.find((option) => option.key === botDifficulty) || BOT_DIFFICULTIES[1];

    const canClickStart = () => {
        if (!user || !currentGame || connecting) {
            return false;
        }

        if (currentGame.owner !== user.username && !(isBotTable && isSeatedHere)) {
            return false;
        }

        // ARCHON: no starting without an opponent (server enforces this too)
        if (Object.values(currentGame.players).length < 2) {
            return false;
        }

        // Lucky Dice games start deckless: the server rolls at start.
        if (
            !isLuckyDice &&
            !Object.values(currentGame.players).every((player) => {
                return !!player.deck.selected;
            })
        ) {
            return false;
        }

        if (waiting && !gameError) {
            return false;
        }

        return true;
    };

    const playerCountInGame = Object.values(currentGame.players || {}).length;
    const myPlayer = Object.values(currentGame.players || {}).find(
        (player) => player.name === user?.username
    );
    const myDeckSelected = !!myPlayer?.deck?.selected;
    // ARCHON: a tournament seat pinned to the event's deck does not choose
    // one - the table selects it. Telling that player to "select a deck"
    // would be pointing them at a picker the server refuses.
    const deckIsPinned = !!currentGame.tournament?.deckLocked;
    const requiresDeckSelection =
        currentGame.gameFormat !== 'sealed' && !isLuckyDice && !myDeckSelected && !deckIsPinned;
    const allPlayersReady =
        playerCountInGame === 2 &&
        (isLuckyDice ||
            Object.values(currentGame.players || {}).every((player) => !!player.deck?.selected));

    const getLiveState = () => {
        if (currentGame.started) {
            return t('In progress');
        }
        if (connecting) {
            return t('Connecting');
        }
        if (playerCountInGame < 2) {
            return t('Waiting');
        }
        if (allPlayersReady) {
            return t('Ready');
        }

        return t('Full');
    };

    const getLiveStateClass = () => {
        const liveState = getLiveState();
        if (liveState === t('Ready')) {
            return 'border-emerald-500/35 bg-emerald-500/12 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300';
        }
        if (liveState === t('Connecting')) {
            return 'border-sky-500/35 bg-sky-500/12 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300';
        }

        return 'border-[color:color-mix(in_oklab,var(--brand)_32%,transparent)] bg-[color:color-mix(in_oklab,var(--brand)_10%,white)] text-[color:color-mix(in_oklab,var(--brand)_82%,black)] dark:border-rose-500/35 dark:bg-rose-500/10 dark:text-rose-300';
    };

    const getLiveDotClass = () => {
        if (allPlayersReady) {
            return {
                ping: 'bg-emerald-400/45',
                dot: 'bg-emerald-400'
            };
        }

        const anyDeckSelected = Object.values(currentGame.players || {}).some(
            (player) => !!player.deck?.selected
        );

        if (playerCountInGame < 2) {
            return {
                ping: anyDeckSelected
                    ? 'bg-[color:color-mix(in_oklab,var(--brand)_34%,transparent)]'
                    : 'bg-[color:color-mix(in_oklab,var(--brand)_48%,transparent)]',
                dot: anyDeckSelected
                    ? 'bg-[color:color-mix(in_oklab,var(--brand)_70%,black)]'
                    : 'bg-[color:color-mix(in_oklab,var(--brand)_84%,black)]'
            };
        }

        return {
            ping: 'bg-[color:color-mix(in_oklab,var(--brand)_48%,transparent)]',
            dot: 'bg-[color:color-mix(in_oklab,var(--brand)_84%,black)]'
        };
    };

    const getStartHint = () => {
        if (connecting) {
            return t('Connecting to game server');
        }

        if (playerCountInGame < 2) {
            return t('Waiting for players');
        }

        if (isLuckyDice) {
            return waiting ? t('Rolling decks...') : t('Decks will be rolled when the game starts');
        }

        const missingDecks = Object.values(currentGame.players || {}).filter(
            (player) => !player.deck?.selected
        ).length;
        if (missingDecks > 0) {
            return t('Waiting for {{count}} player to select decks', { count: missingDecks });
        }

        if (waiting) {
            return t('Starting game...');
        }

        return t('Ready to start');
    };

    const getGameStatus = () => {
        if (gameError) {
            return t(gameError);
        }

        if (connecting) {
            return t('Connecting to game server {{host}}', { host: gameHost });
        }

        if (waiting) {
            return t('Waiting for lobby server...');
        }

        if (Object.values(currentGame.players).length < 2) {
            return t('Waiting for players...');
        }

        if (
            !isLuckyDice &&
            !Object.values(currentGame.players).every((player) => {
                return !!player.deck.selected;
            })
        ) {
            return t('Waiting for players to select decks');
        }

        // ARCHON (F9): nobody is waiting on the bot to press anything - the
        // table starts itself, and the player can start it too.
        if (isBotTable && isSeatedHere) {
            return t('Ready to begin, starting your game against the bot');
        }

        if (currentGame.owner === user.username) {
            return isLuckyDice
                ? t('Ready to begin, Lucky Dice will roll both decks at start')
                : t('Ready to begin, click start to begin the game');
        }

        return isLuckyDice
            ? t('Ready to begin, decks will be rolled when the owner starts the game')
            : t('Ready to begin, waiting for opponent to start the game');
    };

    const formatLabel = (value) => {
        if (!value) {
            return '';
        }

        return value
            .toString()
            .replace(/[-_]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const sendMessage = () => {
        if (message === '') {
            return;
        }

        dispatch(lobbySendMessage('chat', message));
        setMessage('');
    };

    const handleCopyGameLink = async () => {
        const gameLink = `${window.location.protocol}//${window.location.host}/play?gameId=${currentGame.id}`;

        try {
            if (!navigator.clipboard?.writeText) {
                throw new Error('Clipboard unavailable');
            }

            await navigator.clipboard.writeText(gameLink);
            toast.success(t('Game link copied'));
        } catch (error) {
            toast.danger(t('Unable to copy game link'));
        }
    };

    const liveDotClass = getLiveDotClass();

    return (
        <>
            <audio ref={notification}>
                <source src={PlayerJoinedMp3} type='audio/mpeg' />
                <source src={PlayerJoinedOgg} type='audio/ogg' />
            </audio>

            <Panel title={currentGame.name} titleClass='text-base font-semibold tracking-wide'>
                <div className='space-y-3'>
                    <div className='flex flex-wrap items-center gap-2'>
                        <span className='rounded-md border border-border/60 bg-surface-secondary/42 px-2 py-0.5 text-xs text-foreground/75'>
                            {formatLabel(currentGame.gameFormat)}
                        </span>
                        <span className='rounded-md border border-border/60 bg-surface-secondary/42 px-2 py-0.5 text-xs text-foreground/75'>
                            {t('{{players}} / 2 players', { players: playerCountInGame })}
                        </span>
                        {isLuckyDice && (
                            <span
                                className='rounded-md border border-violet-500/35 bg-violet-500/12 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-500/10 dark:text-violet-300'
                                title={t(
                                    'Both players are dealt a random deck from their collection when the game starts'
                                )}
                            >
                                {t('Lucky Dice')}
                            </span>
                        )}
                        {currentGame.sasBound && (
                            <span
                                className='rounded-md border border-amber-500/35 bg-amber-500/12 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-500/10 dark:text-amber-300'
                                title={t(
                                    'Only decks with a SAS rating inside this range can be played'
                                )}
                            >
                                {t('SAS {{min}}-{{max}}', {
                                    min: currentGame.sasBound.min,
                                    max: currentGame.sasBound.max
                                })}
                            </span>
                        )}
                        <span
                            className={`rounded-md border px-2 py-0.5 text-xs font-medium ${getLiveStateClass()}`}
                        >
                            {getLiveState()}
                        </span>
                    </div>

                    <div className='flex items-center gap-2 text-lg font-semibold text-foreground'>
                        <span className='relative inline-flex h-2.5 w-2.5'>
                            <span
                                className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-80 ${liveDotClass.ping}`}
                            />
                            <span
                                className={`relative inline-flex h-2.5 w-2.5 rounded-full ${liveDotClass.dot}`}
                            />
                        </span>
                        <span>{getGameStatus()}</span>
                    </div>

                    {/* ARCHON: which game of which match this table is. Between
                        game one ending and game two starting the players are
                        moved from one table to another, and without this the
                        second looked like a table somebody else had opened. */}
                    {currentGame.tournament && (
                        <AlertPanel type='info' className='!mb-2 !items-center !py-1.5'>
                            <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-sm'>
                                <span className='font-semibold'>
                                    {currentGame.tournament.bestOf > 1
                                        ? t('Game {{game}} of {{bestOf}} - tournament match', {
                                              game: currentGame.tournament.gameNumber || 1,
                                              bestOf: currentGame.tournament.bestOf
                                          })
                                        : t('Tournament match')}
                                </span>
                                <span>
                                    {t(
                                        'Decks are set by the event. The game starts on its own once both players are seated.'
                                    )}
                                </span>
                                <Link
                                    className='underline underline-offset-2 hover:text-foreground'
                                    href={`/tournaments/${currentGame.tournament.tournamentId}`}
                                >
                                    {t('Event page')}
                                </Link>
                            </div>
                        </AlertPanel>
                    )}

                    {requiresDeckSelection && (
                        <AlertPanel
                            type='warning'
                            className='!mb-2 !items-center !py-1.5 [&_[data-slot=indicator]]:self-center [&_[data-slot=content]]:flex-1 [&_[data-slot=content]]:w-full [&_[data-slot=description]]:w-full'
                        >
                            <span className='text-sm'>
                                {t('You need to select a deck before the game can start.')}
                            </span>
                        </AlertPanel>
                    )}

                    {/* ARCHON: the pinned seat's version of the same notice.
                        The deck is loading, not missing - and if it never
                        arrives the player needs to know the picker is not the
                        answer. */}
                    {deckIsPinned && !myDeckSelected && (
                        <AlertPanel
                            type='info'
                            className='!mb-2 !items-center !py-1.5 [&_[data-slot=indicator]]:self-center [&_[data-slot=content]]:flex-1 [&_[data-slot=content]]:w-full [&_[data-slot=description]]:w-full'
                        >
                            <span className='text-sm'>
                                {t(
                                    'Loading the deck you registered for this event. If it does not appear, check it is still in your collection or ask the organizer.'
                                )}
                            </span>
                        </AlertPanel>
                    )}

                    {/* ARCHON (N31): pick who you are practising against.
                        The three styles are the Champion's Challenge's own
                        sparring pilots - one learned brain wearing a different
                        plan - and this is the only screen where the choice can
                        be made, because picking a deck starts the table. */}
                    {isBotTable && isSeatedHere && currentGame.botStyles?.length > 0 && (
                        <div className='mb-2 border-t border-border/60 pt-3'>
                            <div className='mb-1.5 text-[11px] uppercase tracking-wide text-muted'>
                                {t('Opponent style')}
                            </div>
                            <div className='flex flex-wrap gap-1.5'>
                                {[
                                    { key: '', label: t('Its own game'), description: null },
                                    ...currentGame.botStyles
                                ].map((style) => {
                                    const active = (currentGame.botStyle || '') === style.key;

                                    return (
                                        <button
                                            key={style.key || 'none'}
                                            className={[
                                                'rounded-full border px-2.5 py-1 text-xs transition',
                                                active
                                                    ? 'border-accent/60 bg-accent/20 text-amber-200'
                                                    : 'border-border/70 bg-surface-secondary/60 text-foreground hover:border-border'
                                            ].join(' ')}
                                            onClick={() =>
                                                dispatch(
                                                    lobbySendMessage(
                                                        'selectbotstyle',
                                                        currentGame.id,
                                                        style.key
                                                    )
                                                )
                                            }
                                            title={style.description || undefined}
                                            type='button'
                                        >
                                            {style.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className='flex flex-wrap items-center gap-2 border-t border-border/60 pt-3'>
                        <Button
                            variant='primary'
                            isDisabled={!canClickStart()}
                            onPress={() => {
                                setWaiting(true);
                                dispatch(lobbyStartGameRequested(currentGame.id));
                            }}
                        >
                            <Trans>Start</Trans>
                        </Button>
                        <Button
                            variant='tertiary'
                            onPress={() => {
                                dispatch(lobbyLeaveGameRequested(currentGame.id));
                            }}
                        >
                            <Trans>Leave</Trans>
                        </Button>
                        <Button variant='tertiary' onPress={handleCopyGameLink}>
                            <Trans>Copy Game Link</Trans>
                        </Button>
                        {/* ARCHON (F9): the practice difficulty, for whoever
                            joined the bot's table. It re-deals the BOT's deck
                            from a different ARI band, so it only means
                            anything until the game starts. */}
                        {isBotTable && isSeatedHere && !currentGame.started && (
                            <div
                                className='flex items-center gap-1'
                                role='group'
                                aria-label={t('Practice difficulty')}
                            >
                                <span className='me-1 text-xs text-foreground/65 dark:text-muted'>
                                    {t('Difficulty')}
                                </span>
                                {BOT_DIFFICULTIES.map((option) => (
                                    <Button
                                        key={option.key}
                                        size='sm'
                                        variant={
                                            botDifficulty === option.key ? 'primary' : 'tertiary'
                                        }
                                        aria-pressed={botDifficulty === option.key}
                                        onPress={() =>
                                            dispatch(
                                                lobbySendMessage(
                                                    'botdifficulty',
                                                    currentGame.id,
                                                    option.key
                                                )
                                            )
                                        }
                                    >
                                        {t(option.label)}
                                    </Button>
                                ))}
                                <span className='ms-1 text-xs text-foreground/65 dark:text-muted'>
                                    {t('ARI {{range}}', { range: selectedDifficulty.range })}
                                </span>
                            </div>
                        )}
                        <span className='ms-auto text-xs text-foreground/65 dark:text-muted'>
                            {getStartHint()}
                        </span>
                    </div>
                </div>
            </Panel>

            <PendingGamePlayers
                currentGame={currentGame}
                user={user}
                onSelectDeck={() => {
                    // Each selection attempt reports its own outcome - a
                    // rejection from a previous pick must not outlive it.
                    dispatch(lobbyActions.clearGameError());
                    setShowModal(true);
                }}
                onLuckyDice={() => {
                    dispatch(lobbyActions.clearGameError());
                    dispatch(lobbySendMessage('selectrandomdeck', currentGame.id));
                }}
            />

            <Panel
                headerVariant='context'
                title={t('Chat')}
                titleClass='text-xs font-medium tracking-wide text-foreground/75'
            >
                <div
                    className='mb-2 h-36 w-full overflow-y-auto rounded-md border border-border/55 bg-surface-secondary/35 px-3 py-2 text-sm'
                    ref={messageRef}
                    onScroll={() => {
                        setTimeout(() => {
                            if (!messageRef.current) {
                                return;
                            }

                            if (
                                messageRef.current.scrollTop >=
                                messageRef.current.scrollHeight -
                                    messageRef.current.offsetHeight -
                                    20
                            ) {
                                setCanScroll(true);
                            } else {
                                setCanScroll(false);
                            }
                        }, 500);
                    }}
                >
                    <Messages messages={currentGame.messages} />
                </div>
                <form
                    className='mt-2'
                    onSubmit={(event) => {
                        event.preventDefault();
                        sendMessage();
                    }}
                >
                    <Input
                        className='w-full [&_[data-slot="input-wrapper"]]:!bg-surface-secondary/55 [&_[data-slot="input-wrapper"]]:!border [&_[data-slot="input-wrapper"]]:!border-border/65 [&_[data-slot="input-wrapper"]]:!shadow-none [&_[data-slot="input-wrapper"]]:data-[hover=true]:!border-border/80 [&_[data-slot="input-wrapper"]]:data-[focus=true]:!border-border/90'
                        variant='tertiary'
                        type='text'
                        placeholder={t('Enter a message...')}
                        value={message}
                        maxLength={512}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                sendMessage();
                            }
                        }}
                        onChange={(event) => setMessage(event.target.value)}
                    />
                </form>
            </Panel>

            {currentGame.spectators.length > 0 ? (
                <Panel
                    headerVariant='context'
                    title={t('Spectators ({{users}})', {
                        users: currentGame.spectators.length
                    })}
                    titleClass='text-xs font-medium tracking-wide text-foreground/75'
                >
                    <div className='flex flex-wrap gap-x-3 gap-y-1 text-sm text-foreground/85'>
                        {currentGame.spectators.map((spectator) => (
                            <PlayerName key={spectator.name} username={spectator.name} />
                        ))}
                    </div>
                </Panel>
            ) : (
                <div className='rounded-md border border-border/55 bg-surface-secondary/28 px-3 py-1.5 text-xs text-foreground/62 dark:text-muted'>
                    <Trans>Spectators: none</Trans>
                </div>
            )}

            {showModal && (
                <SelectDeckModal
                    expansions={expansions}
                    deckFilter={deckFilter}
                    sasBound={currentGame.sasBound}
                    onClose={() => setShowModal(false)}
                    onDeckSelected={(deck) => {
                        setShowModal(false);
                        dispatch(
                            lobbySendMessage(
                                'selectdeck',
                                currentGame.id,
                                deck.id,
                                deck.isStandalone
                            )
                        );
                    }}
                />
            )}
        </>
    );
};

PendingGame.displayName = 'PendingGame';

export default PendingGame;
